/**
 * Webtech HR Engine™ - Plan Context
 * Context React pour gérer l'accès aux fonctionnalités par plan
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

// Feature definitions
const FEATURES = {
  IA_ENABLED: {
    name: 'Analyse IA',
    free: false,
    pro: true,
    description: 'Analyses intelligentes et recommandations automatisées'
  },
  PERFORMANCE_MODULE: {
    name: 'Module Performance',
    free: false,
    pro: true,
    description: 'HR Annual Performance System™'
  },
  SKILLS_MODULE: {
    name: 'Module Compétences',
    free: false,
    pro: true,
    description: 'HR Skills Intelligence™'
  },
  ADVANCED_REPORTING: {
    name: 'Reporting avancé',
    free: false,
    pro: true,
    description: 'Tableaux de bord et rapports décisionnels'
  },
  PEOPLE_MAPPING: {
    name: 'People Mapping',
    free: true,
    pro: true,
    description: 'Trombinoscope et organigramme'
  },
  AI_SCORING: {
    name: 'Scoring IA',
    free: false,
    pro: true,
    description: 'Calcul intelligent des scores'
  },
  AI_RECOMMENDATIONS: {
    name: 'Recommandations IA',
    free: false,
    pro: true,
    description: 'Recommandations automatisées RH et management'
  }
};

const PlanContext = createContext(null);

export function PlanProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [plan, setPlan] = useState('free');
  const [status, setStatus] = useState('active');
  const [features, setFeatures] = useState({});
  const [activeModules, setActiveModules] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchPlanContext = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/ai/features');
      const data = response.data;
      
      setPlan(data.plan || 'free');
      setStatus(data.status || 'active');
      setFeatures(data.features || {});
      setActiveModules(data.activeModules || {
        // Default FREE plan modules
        peopleMapping: true,
        trombinoscope: true,
        organigramme: true,
        performance: false,
        skills: false,
        aiAnalysis: false,
        advancedReporting: false,
        scoring: false,
        recommendations: false
      });
    } catch (error) {
      console.error('Error fetching plan context:', error);
      // Default to FREE plan on error with proper module access
      setPlan('free');
      setFeatures({
        PEOPLE_MAPPING: true,
        PERFORMANCE_MODULE: false,
        SKILLS_MODULE: false,
        IA_ENABLED: false,
        ADVANCED_REPORTING: false
      });
      setActiveModules({
        peopleMapping: true,
        trombinoscope: true,
        organigramme: true,
        performance: false,
        skills: false,
        aiAnalysis: false,
        advancedReporting: false,
        scoring: false,
        recommendations: false
      });
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchPlanContext();
  }, [fetchPlanContext, user]);

  // Écouter les événements de changement de plan (ex: après paiement, action admin)
  useEffect(() => {
    const handlePlanChange = () => {
      console.log('Plan change event detected, refreshing...');
      fetchPlanContext();
    };

    // Écouter l'événement personnalisé de changement de plan
    window.addEventListener('plan-changed', handlePlanChange);
    
    // Écouter quand la fenêtre reprend le focus (utile après retour de PayTech)
    const handleFocus = () => {
      // Rafraîchir le plan quand l'utilisateur revient sur l'application
      if (isAuthenticated) {
        fetchPlanContext();
      }
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('plan-changed', handlePlanChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchPlanContext, isAuthenticated]);

  // Modèle commercial basé sur le nombre d'employés —
  // toutes les fonctionnalités/modules sont accessibles à tous les plans.
  // `isPro` reste le reflet du vrai plan pour l'affichage (badges, facturation),
  // mais aucune fonctionnalité n'est verrouillée derrière.
  const isPro = plan === 'pro' || plan === 'superadmin';
  const isFree = plan === 'free';
  const isSuperAdmin = plan === 'superadmin' || user?.role === 'SuperAdmin';

  const hasFeature = (_featureKey) => true;
  const hasModule = (_moduleKey) => true;
  const canAccessAI = () => true;
  const canAccessPerformance = () => true;
  const canAccessSkills = () => true;
  const canAccessAdvancedReporting = () => true;
  const canAccessPeopleMapping = () => true;

  const getFeatureInfo = (featureKey) => {
    return FEATURES[featureKey] || null;
  };

  const refreshPlan = () => {
    fetchPlanContext();
  };

  const value = {
    plan,
    status,
    features,
    activeModules,
    loading,
    isPro,
    isFree,
    isSuperAdmin,
    hasFeature,
    hasModule,
    canAccessAI,
    canAccessPerformance,
    canAccessSkills,
    canAccessAdvancedReporting,
    canAccessPeopleMapping,
    getFeatureInfo,
    refreshPlan,
    FEATURES
  };

  return (
    <PlanContext.Provider value={value}>
      {children}
    </PlanContext.Provider>
  );
}

export function usePlan() {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
}

export default PlanContext;
